// REST + WebSocket 客户端
const TOKEN_KEY = "ai-town-token";
const LOGGED_IN_KEY = "ai-town-logged-in";
const ACTIVE_TOWN_KEY = "ai-town-active-town";
const TOWN_DEFAULT_ID = "town_default";

// localStorage 在隐私模式/部分运行时可能不可用,统一容错
export function getToken() { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } }
export function setToken(t: string) { try { localStorage.setItem(TOKEN_KEY, t); } catch { /* 忽略 */ } }
export function clearToken() { try { localStorage.removeItem(TOKEN_KEY); } catch { /* 忽略 */ } }
/** 记录"用户曾经成功登录过",登录/注册页据此默认展示登录界面 */
export function hasLoggedIn(): boolean { try { return localStorage.getItem(LOGGED_IN_KEY) === "1"; } catch { return false; } }
export function markLoggedIn(): void { try { localStorage.setItem(LOGGED_IN_KEY, "1"); } catch { /* 忽略 */ } }
export function getActiveTownId(): string {
  try { return localStorage.getItem(ACTIVE_TOWN_KEY) || ""; } catch { return ""; }
}
export function setActiveTownId(townId: string): void {
  try {
    if (townId && townId !== TOWN_DEFAULT_ID) localStorage.setItem(ACTIVE_TOWN_KEY, townId);
    else localStorage.removeItem(ACTIVE_TOWN_KEY);
  } catch { /* 忽略 */ }
}

/** 将旧版默认镇路径映射到显式镇上下文，仅在设置了非默认当前镇时生效。 */
function townPath(path: string, townId = getActiveTownId()): string {
  const id = townId || TOWN_DEFAULT_ID;
  if (!path.startsWith("/api/") || id === TOWN_DEFAULT_ID) return path;
  const prefix = `/api/towns/${encodeURIComponent(id)}`;
  const rules: [RegExp, string][] = [
    [/^\/api\/world$/, "/world"],
    [/^\/api\/map(\?.*)?$/, "/map$1"],
    [/^\/api\/facilities$/, "/facilities"],
    [/^\/api\/feed(\?.*)?$/, "/feed$1"],
    [/^\/api\/agents\//, "/agents/"],
    [/^\/api\/chat\//, "/chat/"],
    [/^\/api\/chat$/, "/chat"],
    [/^\/api\/agent\/create$/, "/agent/create"],
    [/^\/api\/agent\/goals$/, "/agent/goals"],
    [/^\/api\/agent\/gender-options$/, "/agent/gender-options"],
    [/^\/api\/board$/, "/board"],
    [/^\/api\/reports/, "/reports"],
    [/^\/api\/activity-log/, "/activity-log"],
    [/^\/api\/relationships/, "/relationships"],
    [/^\/api\/topics/, "/topics"],
    [/^\/api\/evolution$/, "/evolution"],
    [/^\/api\/personalities$/, "/personalities"],
    [/^\/api\/thoughts/, "/thoughts"],
    [/^\/api\/timeline/, "/timeline"],
    [/^\/api\/resources/, "/resources"],
    [/^\/api\/professions$/, "/professions"],
    [/^\/api\/reputation\/events/, "/reputation/events"],
    [/^\/api\/events$/, "/events"],
    [/^\/api\/stats\/usage/, "/stats/usage"],
  ];
  for (const [pattern, replacement] of rules) {
    if (pattern.test(path)) return `${prefix}${path.replace(pattern, replacement)}`;
  }
  return path;
}

async function req(path: string, opts: RequestInit = {}) {
  const res = await fetch(path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `请求失败(${res.status})`) as Error & { status?: number; code?: string; data?: any };
    error.status = res.status;
    error.code = data.error;
    error.data = data;
    throw error;
  }
  return data;
}

// ---------- 密码前端哈希 ----------
// 密码不在前端之外以明文出现:先拼接固定 pepper 串(规避彩虹表)再做 SHA-256,
// 服务器只接收 64 位十六进制哈希,存储时再套一层 bcrypt。
const PASSWORD_PEPPER = "town.game4ai.online";
export async function hashPassword(plain: string): Promise<string> {
  const data = new TextEncoder().encode(plain + PASSWORD_PEPPER);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const api = {
  register: async (email: string, username: string, password: string) =>
    req("/api/register", { method: "POST", body: JSON.stringify({ email, username, password: await hashPassword(password) }) }),
  login: async (account: string, password: string) =>
    req("/api/login", { method: "POST", body: JSON.stringify({ account, password: await hashPassword(password) }) }),
  verifyEmail: (email: string, code: string) =>
    req("/api/verify-email", { method: "POST", body: JSON.stringify({ email, code }) }),
  resendVerify: (email: string) =>
    req("/api/resend-verify", { method: "POST", body: JSON.stringify({ email }) }),
  forgotPassword: (email: string) =>
    req("/api/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: async (email: string, code: string, password: string) =>
    req("/api/reset-password", { method: "POST", body: JSON.stringify({ email, code, password: await hashPassword(password) }) }),
  me: () => req("/api/me"),
  changePassword: async (currentPassword: string, newPassword: string) =>
    req("/api/change-password", {
      method: "POST",
      body: JSON.stringify({
        currentPassword: await hashPassword(currentPassword),
        newPassword: await hashPassword(newPassword),
      }),
    }),
  createAgent: (data: any, townId?: string) => req(townPath("/api/agent/create", townId), { method: "POST", body: JSON.stringify(data) }),
  feed: (before?: number, townId?: string) => req(townPath(`/api/feed${before ? `?before=${before}` : ""}`, townId)),
  profile: (id: string, townId?: string) => req(townPath(`/api/agents/${encodeURIComponent(id)}`, townId)),
  memories: (agentId: string, offset: number, limit: number, townId?: string) =>
    req(townPath(`/api/agents/${encodeURIComponent(agentId)}/memories?offset=${offset}&limit=${limit}`, townId)),
  chatHistory: (agentId: string, townId?: string) => req(townPath(`/api/chat/${encodeURIComponent(agentId)}`, townId)),
  chat: (agentId: string, text: string, townId?: string) => req(townPath("/api/chat", townId), { method: "POST", body: JSON.stringify({ agentId, text }) }),
  setGoals: (data: any, townId?: string) => req(townPath("/api/agent/goals", townId), { method: "POST", body: JSON.stringify(data) }),
  guestJoin: (name?: string, gender?: string, personality?: string, townId = getActiveTownId()) =>
    req("/api/guest/join", { method: "POST", body: JSON.stringify({ name: name || "", gender, personality, townId: townId || undefined }) }),
  genderOptions: (townId?: string) => req(townPath("/api/agent/gender-options", townId)),
  activityLog: (agentId?: string, limit?: number, townId?: string) => {
    const q = new URLSearchParams();
    if (agentId) q.set("agentId", agentId);
    if (limit) q.set("limit", String(limit));
    const s = q.toString();
    return req(townPath(`/api/activity-log${s ? `?${s}` : ""}`, townId));
  },
  board: (townId?: string) => req(townPath("/api/board", townId)),
  postBoard: (content: string, townId?: string) => req(townPath("/api/board", townId), { method: "POST", body: JSON.stringify({ content }) }),
  feedback: (data: { category: string; content: string; contact?: string }) =>
    req("/api/feedback", { method: "POST", body: JSON.stringify(data) }),
  facilities: (townId?: string) => req(townPath("/api/facilities", townId)),
  relationships: (limit?: number, townId?: string) => req(townPath(`/api/relationships${limit ? `?limit=${limit}` : ""}`, townId)),
  relationshipEvents: (limit?: number, townId?: string) => req(townPath(`/api/relationships/events${limit ? `?limit=${limit}` : ""}`, townId)),
  topics: (townId?: string) => req(townPath("/api/topics", townId)),
  topicPropagation: (topicId?: string, townId?: string) => req(townPath(`/api/topics/propagation${topicId ? `?topicId=${topicId}` : ""}`, townId)),
  evolution: (townId?: string) => req(townPath("/api/evolution", townId)),
  personalities: (townId?: string) => req(townPath("/api/personalities", townId)),
  thoughts: (agentId?: string, townId?: string) => req(townPath(`/api/thoughts${agentId ? `?agentId=${agentId}` : ""}`, townId)),
  timeline: (limit?: number, townId?: string) => req(townPath(`/api/timeline${limit ? `?limit=${limit}` : ""}`, townId)),
  experiments: () => req("/api/experiments"),
  webmcpState: (townId = getActiveTownId()) => req(`/api/webmcp/state${townId ? `?townId=${encodeURIComponent(townId)}` : ""}`),
  webmcpMove: (x: number, y: number, townId?: string) => req("/api/webmcp/move", { method: "POST", body: JSON.stringify({ x, y, ...(townId ? { townId } : {}) }) }),
  webmcpSay: (text: string, townId?: string) => req("/api/webmcp/say", { method: "POST", body: JSON.stringify({ text, ...(townId ? { townId } : {}) }) }),
  webmcpEnter: (townId?: string) => req("/api/webmcp/enter", { method: "POST", body: JSON.stringify(townId ? { townId } : {}) }),
  webmcpExit: (townId?: string) => req("/api/webmcp/exit", { method: "POST", body: JSON.stringify(townId ? { townId } : {}) }),
  usage: (hours = 24, townId?: string) => req(townPath(`/api/stats/usage?hours=${hours}`, townId)),
  events: (townId?: string) => req(townPath("/api/events", townId)),
  resources: (agentId?: string, townId?: string) => req(townPath(`/api/resources${agentId ? `?agentId=${encodeURIComponent(agentId)}` : ""}`, townId)),
  professions: (townId?: string) => req(townPath("/api/professions", townId)),
  reputationEvents: (day?: number, townId?: string) => req(townPath(`/api/reputation/events${day ? `?day=${day}` : ""}`, townId)),
  reports: (townId?: string) => req(townPath("/api/reports", townId)),
  report: (day: number, townId?: string) => req(townPath(`/api/reports/${day}`, townId)),
  gazette: (day?: number) => req(`/api/gazette${day ? `?day=${day}` : ""}`),
  map: (mapId?: string, townId?: string) => req(townPath(`/api/map${mapId ? `?mapId=${mapId}` : ""}`, townId)),
  status: () => req("/api/status"),
  towns: () => req("/api/towns"),
  town: (townIdOrSlug: string) => req(`/api/towns/${encodeURIComponent(townIdOrSlug)}`),
  relocationPreview: (townIdOrSlug: string) =>
    req(`/api/towns/${encodeURIComponent(townIdOrSlug)}/relocation/preview`),
  relocateToTown: (townIdOrSlug: string) =>
    req(`/api/towns/${encodeURIComponent(townIdOrSlug)}/relocation`, { method: "POST", body: "{}" }),
  enterTown: (townId: string) => req(`/api/towns/${encodeURIComponent(townId)}/enter`, { method: "POST", body: "{}" }),
  consoleTowns: () => req("/api/console/towns"),
  consoleOverview: (townId: string) => req(`/api/console/towns/${encodeURIComponent(townId)}/overview`),
  consoleResidents: (townId: string, query = "") => req(`/api/console/towns/${encodeURIComponent(townId)}/residents${query ? `?${query}` : ""}`),
  consoleResident: (townId: string, agentId: string) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/residents/${encodeURIComponent(agentId)}`),
  consoleRemoveResident: (townId: string, agentId: string) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/residents/${encodeURIComponent(agentId)}/remove`, { method: "POST", body: "{}" }),
  consoleBan: (townId: string, userId: string) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/members/${encodeURIComponent(userId)}/ban`, { method: "POST", body: "{}" }),
  consoleUnban: (townId: string, userId: string) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/members/${encodeURIComponent(userId)}/unban`, { method: "POST", body: "{}" }),
  consoleUpdateSettings: (townId: string, data: any) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/settings`, { method: "PATCH", body: JSON.stringify(data) }),
  consoleWallet: (townId: string, limit = 50, offset = 0) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/wallet?limit=${limit}&offset=${offset}`),
  consoleRecharge: (townId: string, amount: number) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/wallet/recharge`, { method: "POST", body: JSON.stringify({ amount }) }),
  consoleSleep: (townId: string) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/sleep`, { method: "POST", body: "{}" }),
  consoleWake: (townId: string) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/wake`, { method: "POST", body: "{}" }),
  consolePosts: (townId: string) => req(`/api/console/towns/${encodeURIComponent(townId)}/moderation/posts`),
  consoleDeletePost: (townId: string, postId: number) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/moderation/posts/${postId}`, { method: "DELETE" }),
  consoleBoard: (townId: string) => req(`/api/console/towns/${encodeURIComponent(townId)}/moderation/board`),
  consoleDeleteBoard: (townId: string, messageId: number) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/moderation/board/${messageId}`, { method: "DELETE" }),
  consoleAudit: (townId: string, limit = 100) =>
    req(`/api/console/towns/${encodeURIComponent(townId)}/audit?limit=${limit}`),
  adminOverview: () => req("/api/admin/overview"),
  adminTowns: (query = "") => req(`/api/admin/towns${query ? `?${query}` : ""}`),
  adminCreateTown: (data: any) => req("/api/admin/towns", { method: "POST", body: JSON.stringify(data) }),
  adminTown: (townId: string) => req(`/api/admin/towns/${encodeURIComponent(townId)}`),
  adminUpdateTown: (townId: string, data: any) =>
    req(`/api/admin/towns/${encodeURIComponent(townId)}`, { method: "PATCH", body: JSON.stringify(data) }),
  adminAssignTown: (townId: string, ownerUserId: string | null) =>
    req(`/api/admin/towns/${encodeURIComponent(townId)}/assign`, {
      method: "POST",
      body: JSON.stringify({ ownerUserId }),
    }),
  adminAdjustWallet: (townId: string, delta: number, memo: string) =>
    req(`/api/admin/towns/${encodeURIComponent(townId)}/wallet`, {
      method: "POST",
      body: JSON.stringify({ delta, memo }),
    }),
  adminCloseTown: (townId: string) =>
    req(`/api/admin/towns/${encodeURIComponent(townId)}`, {
      method: "DELETE",
      body: JSON.stringify({ confirm: true }),
    }),
  adminMayors: () => req("/api/admin/mayors"),
  adminCreateMayor: (email: string, username: string) =>
    req("/api/admin/mayors", { method: "POST", body: JSON.stringify({ email, username }) }),
  adminResendInvite: (userId: string) =>
    req(`/api/admin/mayors/${encodeURIComponent(userId)}/resend-invite`, { method: "POST", body: "{}" }),
  adminUsers: (query = "") => req(`/api/admin/users${query ? `?${query}` : ""}`),
  adminAudit: (query = "") => req(`/api/admin/audit${query ? `?${query}` : ""}`),
  adminHealth: () => req("/api/admin/tenancy/health"),
  adminBilling: (hours = 24) => req(`/api/admin/tenancy/billing?hours=${hours}`),
};

// ---------- WebSocket ----------
type WSHandler = (msg: any) => void;

export class TownSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<WSHandler>();
  private retry = 0;
  private closed = false;
  private townId: string;

  constructor(townId = "") {
    this.townId = townId;
  }

  connect() {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      ws.send(JSON.stringify({ type: "auth", token: getToken(), ...(this.townId ? { townId: this.townId } : {}) }));
    };
    ws.onmessage = (e) => {
      let msg: any;
      try { msg = JSON.parse(e.data); } catch { return; }
      for (const h of this.handlers) h(msg);
    };
    ws.onclose = () => {
      if (this.closed) return;
      this.retry++;
      setTimeout(() => this.connect(), Math.min(1000 * this.retry, 8000));
    };
  }
  on(h: WSHandler) { this.handlers.add(h); }
  send(msg: any) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(msg));
  }
  close() { this.closed = true; this.ws?.close(); }
}
